import { Component, DestroyRef, Injector, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Event as RouterEvent, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { PlatformTaskInterfaceStateService } from '../../shared/services/platform-task-interface-state.service';
import { AdminService } from '../../admin/services/admin.service';
import { takeUntil, filter, debounceTime } from 'rxjs/operators';
import { Observable, Subject, merge } from 'rxjs';
import { TranslateService } from '../../shared/services/translate.service';
import { NotificationService, contentLanguageUpdated } from '../../shared/services/notification.service';
import { ActivityLogAccessArgs, ActivityLogService } from '../../shared/services/activity-log.service';
import { AppStateService, ActivePage } from '../../shared/services/app-state.service';
import { SubsectionService } from '../../shared/services/subsection.service';
import { TreeManagerService } from '../../project-subsection/services/tree-manager.service';
import {
  BaseTreeNode,
  isShareLinkAssetNode,
  isSubsectionFileNode,
  TreeNodeType,
} from '../../project-subsection/models/tree-node';
import { ShareLinkAssetEntityTypes } from '../../shared/interfaces/share-link.interface';
import { ReleaseTreeState, TreeState } from '../../project-subsection/models';
import { CommentPanelWidthService } from '../../shared/services/comment-panel-width.service';
import { CommentVisibilityService } from '../../shared/services/comment-visibility.service';
import { TopMediaInfoService } from '../../shared/components/top-media-info/top-media-info.service';
import { ProjectService, onProjectBasicInfoLoaded } from '../../shared/services/project.service';
import { MtmContextService } from '@mtm/context-ui-angular';
import {
  VIEW_CONTEXT_KEY,
  ViewContextFlags,
  ViewContextKey,
} from '../../shared/services/view-context-keys';
import { ActiveSubsectionState, OptionVersionSimple } from '../../shared/interfaces';
import { SubsectionPreviewComponent } from '../../project-subsection/preview/subsection-preview.component';
import { ReleaseTreeService } from '../../project-subsection/services/release-tree-service';
import { DriveCommentPanelService } from '../../shared/drive/drive-comment-panel.service';
import { DriveRightRailService } from '../../shared/drive/drive-right-rail.service';
import { PlatformCommentsChatStateService } from '../../shared/services/platform-comments-chat-state.service';
import { DriveCompareUiService } from '../../shared/drive/drive-compare-ui.service';
import { DriveTreeUiService } from '../../shared/drive/drive-tree-ui.service';

@Component({
  selector: 'top-nav',
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TopNavComponent implements OnInit, OnDestroy {
  publicReviewLink: boolean = false;
  publicPage: boolean = false;
  authUser: any;
  ngUnsubscribe = new Subject();
  project: any;
  projectId: string = null;
  selectedLanguage: string = 'en_US';
  selectedFlag: string = 'flag-icon-gb';
  isPending: boolean = false;
  hasActivityLogAccess: boolean = false;
  isShareLinkPreview: boolean = false;
  isReleasePreview: boolean = false;
  isCommentFeatureAllowed: boolean = false;
  isTaskFeatureAllowed: boolean = false;
  isMySpacePage: boolean = false;
  subSectionState: ActiveSubsectionState;
  treeNodeType: typeof TreeNodeType = TreeNodeType;

  constructor(
    private readonly adminService: AdminService,
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly activityLogService: ActivityLogService,
    private readonly route: ActivatedRoute,
    private readonly subsectionService: SubsectionService,
    private readonly router: Router
  ) {}

  private readonly appState = inject(AppStateService);
  private readonly treeManagerService = inject(TreeManagerService);
  private readonly commentPanelWidthService = inject(CommentPanelWidthService);
  private readonly commentVisibilityService = inject(CommentVisibilityService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly projectService = inject(ProjectService);
  private readonly releaseTreeService: ReleaseTreeService = inject(ReleaseTreeService);
  private readonly driveCommentPanelService: DriveCommentPanelService = inject(DriveCommentPanelService);
  protected readonly driveRightRail: DriveRightRailService = inject(DriveRightRailService);
  private readonly platformTaskState: PlatformTaskInterfaceStateService = inject(PlatformTaskInterfaceStateService);
  private readonly platformChatState: PlatformCommentsChatStateService = inject(PlatformCommentsChatStateService);
  private readonly driveCompareUiService: DriveCompareUiService = inject(DriveCompareUiService);
  private readonly driveTreeUi: DriveTreeUiService = inject(DriveTreeUiService);
  private readonly viewContext: MtmContextService = inject(MtmContextService);
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private releaseRecord: Record<string, any> = {};

  /** Previous toggle snapshot, used to notice which toggle the user just flipped. */
  private lastViewToggleState: ViewContextFlags = {};

  /** URL value already acted on per toggle, so a param is applied on change, not on every tick. */
  private readonly appliedViewParams: ViewContextFlags = {};
  get subSectionPreview(): SubsectionPreviewComponent | null {
    return this.topMediaInfoService.getSubsectionPreview();
  }

  get votingVisible(): boolean {
    return !this.subSectionPreview?.activePage;
  }

  get shouldDisableInteractiveElements(): boolean {
    return this.hasLockedVersionInCurrentOption || this.isReleasePreview;
  }

  get hasLockedVersionInCurrentOption(): boolean {
    if (!this.subSectionState?.activeOption?.simpleVersions || !this.subSectionState?.activeOption?.optionId) {
      return false;
    }

    // Check if any version in the current option is locked
    return this.subSectionState.activeOption.simpleVersions.some((version: OptionVersionSimple) => {
      const versionId: string = `${this.subSectionState?.activeOption?.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  ngOnInit() {
    this.authUser = this.authService.getAuthUser();
    this.publicReviewLink = this.route.snapshot.data.publicReviewLink;
    this.publicPage = this.route.snapshot.data.publicPage;
    this.selectedLanguage = localStorage.getItem('auth_user_language') || 'en_us';
    this.adjustSelectedFlag();
    contentLanguageUpdated.pipe(takeUntil(this.ngUnsubscribe)).subscribe((language: string) => {
      this.changeLang(language, false);
    });
    this.activityLogService.activityLogAccess
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((args: ActivityLogAccessArgs) => {
        this.hasActivityLogAccess = args.hasAccess;
      });

    // Subscribe to release tree service to get release record
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state: ReleaseTreeState) => {
          this.releaseRecord = state?.releaseRecord || {};
        },
      });

    this.subsectionService.uiState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state) => {
      this.isShareLinkPreview = !!state?.isShareLinkPreview;
      this.isReleasePreview = !!state?.isReleasePreview;
    });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((subsectionState: ActiveSubsectionState | null) => !!subsectionState?.activeFile)
      )
      .subscribe((subsectionState: ActiveSubsectionState) => {
        this.subSectionState = subsectionState;
      });

    this.router.events
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.updateCommentFeatureVisibility(event.urlAfterRedirects);
      });

    this.updateCommentFeatureVisibility(this.router.url);
    this.initViewContextSync();
  }

  /**
   * Keeps the `lv` / `ts` / `tv` / `cm` / `co` context flags and the actual
   * toggle state in step, in both directions. Every state holder behind the
   * toggles is merged into one stream so a change from any source — these
   * buttons, the media-info bar, the drive explorer — is stored too.
   *
   * The context state is in the merge because loading one is not a navigation:
   * a `?ctx=` hydrated after this component is up would otherwise never be
   * applied to the toggles.
   */
  private initViewContextSync(): void {
    const navigationEnd$: Observable<NavigationEnd> = this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
    );

    merge(
      navigationEnd$,
      toObservable(this.viewContext.state, { injector: this.injector }),
      onProjectBasicInfoLoaded,
      this.topMediaInfoService.state$(),
      this.subsectionService.uiState$,
      toObservable(this.appState.commentPageState, { injector: this.injector }),
      toObservable(this.commentPanelWidthService.width, { injector: this.injector }),
      toObservable(this.driveCommentPanelService.active, { injector: this.injector }),
      toObservable(this.driveCommentPanelService.savedActive, { injector: this.injector }),
      toObservable(this.driveRightRail.hasTasks, { injector: this.injector }),
      toObservable(this.driveRightRail.visible, { injector: this.injector }),
      toObservable(this.driveCompareUiService.showCompareToggle, { injector: this.injector }),
      toObservable(this.driveCompareUiService.compareActive, { injector: this.injector }),
      toObservable(this.driveTreeUi.savedExpanded, { injector: this.injector }),
      this.platformTaskState.context$
    )
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(0))
      .subscribe(() => {
        this.applyViewContext();
        this.syncViewContext();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  get isSubsectionPage(): boolean {
    return this.appState.getActivePage() === ActivePage.ProjectSubsection;
  }

  get isMediaPreviewCommentsChatShowing(): boolean {
    return (
      this.isCommentFeatureAllowed &&
      this.isCommentVisible &&
      !this.isShareLinkPreview &&
      !this.isReleasePreview &&
      !this.isDashboard
    );
  }

  get canShowTaskIcon(): boolean {
    return this.isTaskFeatureAllowed && this.appState.isEnableTaskPage() && !!this.appState.getActivePage();
  }

  get isPlatformTaskShowing(): boolean {
    return !this.publicPage && !!this.authUser && this.canShowTaskIcon;
  }

  get isMediaPreviewTasksChatShowing(): boolean {
    return (
      this.isTaskFeatureAllowed &&
      this.isTaskVisible &&
      !this.isShareLinkPreview &&
      !this.isReleasePreview &&
      !this.isDashboard
    );
  }

  get canShowCommentIcon(): boolean {
    return this.isCommentFeatureAllowed && this.appState.isEnableCommentPage() && !!this.appState.getActivePage();
  }

  get treeState(): TreeState<BaseTreeNode> {
    return this.treeManagerService.getCurrentState();
  }

  get isCommentVisible(): boolean {
    if (!this.isCommentFeatureAllowed || !this.treeState?.selectedNode) {
      return false;
    }
    const { selectedNode } = this.treeState;
    return (
      isSubsectionFileNode(selectedNode) ||
      (isShareLinkAssetNode(selectedNode) && selectedNode.assetType === ShareLinkAssetEntityTypes.FILE)
    );
  }

  get isTaskVisible(): boolean {
    if (!this.isCommentFeatureAllowed || !this.treeState?.selectedNode) {
      return false;
    }
    const { selectedNode } = this.treeState;
    return (
      isSubsectionFileNode(selectedNode) ||
      (isShareLinkAssetNode(selectedNode) && selectedNode.assetType === ShareLinkAssetEntityTypes.FILE)
    );
  }

  get isPlatformCommentsChatShowing(): boolean {
    return (
      this.isCommentFeatureAllowed &&
      !this.publicReviewLink &&
      !this.publicPage &&
      !!this.authUser &&
      this.canShowCommentIcon
    );
  }

  get isDashboard(): boolean {
    return this.appState.getActivePage() === ActivePage.ProjectDashboard;
  }

  get isNavigatingToProjectDashboard(): boolean {
    return this.appState.isNavigatingToProjectDashboard();
  }

  get isCommentUiInitializing(): boolean {
    return (
      this.isNavigatingToProjectDashboard ||
      (!this.isPlatformCommentsChatShowing && !this.isMediaPreviewCommentsChatShowing)
    );
  }

  get hasCommentPanelWidth(): boolean {
    return this.commentPanelWidthService.width() > 0;
  }

  get isCommentExpanded(): boolean {
    return this.isRightPanelExpandedFor(this.isPlatformCommentsChatShowing, this.isMediaPreviewCommentsChatShowing);
  }

  /**
   * Task-specific counterpart to `isCommentExpanded`. Gated on the task
   * "showing" flags rather than the comment ones: on My Own Space,
   * `isPlatformCommentsChatShowing`/`isMediaPreviewCommentsChatShowing` are
   * always false (no legacy comment button there), so reusing
   * `isCommentExpanded` for the task icon left it permanently un-highlighted
   * even while the task panel was open.
   */
  get isTaskExpanded(): boolean {
    return this.isRightPanelExpandedFor(this.isPlatformTaskShowing, this.isMediaPreviewTasksChatShowing);
  }

  /** Shared expanded/collapsed check behind `isCommentExpanded` and `isTaskExpanded` — see `openTask`/`openChat`, which write the same per-page flag for whichever panel is active. */
  private isRightPanelExpandedFor(platformShowing: boolean, mediaPreviewShowing: boolean): boolean {
    const activePage = this.appState.getActivePage();
    return (
      (platformShowing && (activePage ? this.appState.isCommentPageCommentExpanded(activePage) : false)) ||
      (!this.subsectionService.uiState?.commentsCollapsed && mediaPreviewShowing)
    );
  }

  get isListViewMode(): boolean {
    return this.topMediaInfoService.isListViewMode;
  }

  get showListViewToggleButton(): boolean {
    return this.topMediaInfoService.showListViewModeButton;
  }

  public toggleListView(): void {
    this.topMediaInfoService.updateState({
      isListViewMode: !this.topMediaInfoService.isListViewMode,
    });
  }

  /**
   * On/off value of each URL-backed toggle. A key is omitted when its control
   * isn't available on the current page — the conditions mirror the `@if`
   * guards around each button in the template, so an omitted key means the
   * query param has nothing to act on and is neither applied nor written.
   *
   * On a drive surface `cm` and `tv` are the explorer's SAVED feed / tree state,
   * not the live one: a page loaded at phone width starts with them closed
   * without saving it (drive's `narrow-load-close.ts`), and a link minted there
   * must not carry `cm=0` / `tv=0` to a desktop. The buttons keep the live state.
   */
  private get viewToggleState(): ViewContextFlags {
    const state: ViewContextFlags = {};

    if (this.isSubsectionPage && this.showListViewToggleButton) {
      state[VIEW_CONTEXT_KEY.listView] = this.isListViewMode;
    }
    if (this.isSubsectionPage) {
      const tree: boolean | undefined = this.treeToggleState;
      if (tree !== undefined) {
        state[VIEW_CONTEXT_KEY.treeView] = tree;
      }
    }

    const task: boolean | undefined = this.taskToggleState;
    if (task !== undefined) {
      state[VIEW_CONTEXT_KEY.task] = task;
    }
    const comment: boolean | undefined = this.commentToggleState;
    if (comment !== undefined) {
      state[VIEW_CONTEXT_KEY.comment] = comment;
    }
    if (this.driveCompareUiService.showCompareToggle()) {
      state[VIEW_CONTEXT_KEY.compare] = this.driveCompareUiService.compareActive();
    }

    return state;
  }

  /**
   * What each toggle would be on a fresh load with no query params. A param at
   * its default is dropped from the URL rather than written, so these values are
   * what "absent" reads back as and must track the real initial state — the
   * value the page settles at after its own init, not a field initialiser.
   *
   * A key left out has no known default and so is always written out in full.
   */
  private get viewToggleDefaults(): ViewContextFlags {
    const defaults: ViewContextFlags = {
      [VIEW_CONTEXT_KEY.listView]: false,
      [VIEW_CONTEXT_KEY.task]: false,
      [VIEW_CONTEXT_KEY.compare]: false,
      [VIEW_CONTEXT_KEY.treeView]: true,
      // Open on both surfaces: off drive the right panel starts expanded on
      // 'comment', and on drive the explorer's `showSidebar` starts open. The
      // drive lib can override that from a persisted per-user preference, so a
      // user who has stored "closed" will not get `cm=1` pinned in their URL.
      [VIEW_CONTEXT_KEY.comment]: true,
    };

    // Off drive, `ts` and `cm` are two views of one right panel, so `ts=1`
    // already states the comment panel is closed and `cm=0` beside it carries
    // nothing. `applyViewContext` reads it back the same way: `ts` is
    // applied first and `openTask` closes the comment panel on its own.
    if (!this.showDriveSurface && this.taskToggleState === true) {
      defaults[VIEW_CONTEXT_KEY.comment] = false;
    }

    return defaults;
  }

  /**
   * On a drive surface `tv` mirrors the explorer's saved tree state (drive-fe ADR-0044) —
   * one state at every width, `undefined` until the explorer has reported, so no
   * placeholder is applied or written. Elsewhere it is the legacy subsection explorer's
   * `explorerCollapsed`.
   */
  private get treeToggleState(): boolean | undefined {
    if (this.driveTreeUi.active()) {
      return this.driveTreeUi.savedExpanded();
    }
    return !this.subsectionService.uiState?.explorerCollapsed;
  }

  /**
   * Mirrors the task button's `@if` guard; `undefined` when it isn't rendered. On drive
   * surfaces the button lives on the right rail instead, so `ts` mirrors that one.
   */
  private get taskToggleState(): boolean | undefined {
    if (this.isProjectTaskSurfacePending) {
      return undefined;
    }
    // A drive rail without Tasks (My Own Space, compare) has no task state to mirror.
    if (this.driveRightRail.visible()) {
      return this.driveRightRail.hasTasks() ? this.driveRightRail.tasksOpen() : undefined;
    }
    if (this.isMySpacePage) {
      return undefined;
    }
    const showing: boolean = this.isPlatformTaskShowing || this.isMediaPreviewTasksChatShowing;
    if (!showing || !this.hasCommentPanelWidth) {
      return undefined;
    }
    return this.isTaskExpanded && this.rightViewMode === 'task';
  }

  /**
   * Off drive, mirrors the comment button's `@if` guards. On a drive surface the
   * top-nav comment button is gone (STUDIO-2249), so `cm` now mirrors the drive
   * panel itself: the explorer's own in-content toolbar toggle is what moves it,
   * and `syncCommentsFromExplorer` on the drive surface feeds every such flip
   * back into `DriveCommentPanelService`. Keeping the key is what lets a
   * shared `cm` deep link still open the panel. It reads the panel's saved
   * state (`savedActive`), so a phone load's unsaved close is never written.
   * The `showing` gate is retained unchanged so the set of pages that write
   * `cm` is exactly what it was.
   */
  private get commentToggleState(): boolean | undefined {
    const showing: boolean = this.isPlatformCommentsChatShowing || this.isMediaPreviewCommentsChatShowing;
    if (!showing) {
      return undefined;
    }
    if (this.showDriveSurface) {
      return this.driveCommentPanelService.savedActive();
    }
    if (!this.hasCommentPanelWidth) {
      return undefined;
    }
    return this.isCommentExpanded && this.rightViewMode === 'comment';
  }

  /**
   * Drives the toggles from the URL by reusing the same handlers a click would,
   * so a query param can never reach a state the buttons can't. Toggles whose
   * param is absent keep their default.
   *
   * `ts` and `cm` are mutually exclusive only off drive, where both are views of
   * one right panel — there `?ts=1&cm=1` settles on the comment panel, since
   * `ts` is applied first. On a drive surface `cm` is the independent
   * `DriveCommentPanelService`, so both can legitimately be on at once.
   *
   * `co` covers the drive compare toggle only: outside drive, "compare" is a
   * navigation to the `/compare` route rather than a state you can flip, so
   * there is nothing for an on/off param to drive.
   */
  private applyViewContext(): void {
    const desired: ViewContextFlags = this.viewContext.readAll();

    /**
     * A param is acted on when its URL value is new to us, and only while its
     * control exists — so a toggle that isn't rendered yet is retried later.
     * Consuming the value here is what stops a stale `lv=1` from undoing the
     * user's next click before `syncViewContext` can rewrite the URL.
     *
     * The live state is re-read per key rather than snapshotted up front: `ts`
     * and `cm` share one right panel, so opening the task panel already closed
     * the comment panel, and a stale reading would "correct" that back.
     */
    const differs: (key: ViewContextKey) => boolean = (key: ViewContextKey): boolean => {
      const target: boolean | undefined = desired[key];
      if (target === undefined) {
        delete this.appliedViewParams[key];
        return false;
      }
      const current: boolean | undefined = this.viewToggleState[key];
      if (current === undefined || this.appliedViewParams[key] === target) {
        return false;
      }
      this.appliedViewParams[key] = target;
      return current !== target;
    };

    if (differs(VIEW_CONTEXT_KEY.listView)) {
      this.toggleListView();
    }
    if (differs(VIEW_CONTEXT_KEY.treeView)) {
      if (this.driveTreeUi.active()) {
        this.driveTreeUi.setTreeExpanded(!!desired[VIEW_CONTEXT_KEY.treeView]);
      } else {
        this.subsectionService.updateUIState({ explorerCollapsed: !desired[VIEW_CONTEXT_KEY.treeView] });
      }
    }
    if (differs(VIEW_CONTEXT_KEY.task)) {
      if (this.driveRightRail.hasTasks()) {
        this.driveRightRail.setTasksOpen(!!desired[VIEW_CONTEXT_KEY.task]);
      } else if (!this.driveRightRail.visible()) {
        this.openTask();
      }
    }
    if (differs(VIEW_CONTEXT_KEY.comment)) {
      if (this.showDriveSurface) {
        this.driveCommentPanelService.setActive(!!desired[VIEW_CONTEXT_KEY.comment]);
      } else {
        this.openChat();
      }
    }
    if (differs(VIEW_CONTEXT_KEY.compare)) {
      this.driveCompareUiService.toggleCompare();
    }
  }

  /** Writes the live toggle state back into the URL, once a toggle has moved. */
  private syncViewContext(): void {
    const state: ViewContextFlags = this.viewToggleState;
    const moved: ViewContextKey[] = (Object.keys(state) as ViewContextKey[]).filter(
      (key: ViewContextKey) =>
        this.lastViewToggleState[key] !== undefined && this.lastViewToggleState[key] !== state[key]
    );

    // Off drive, flipping `ts` is a move on `cm` as well: the two are views of
    // one panel, so `cm` can hold its value while the default it is compared
    // against flips (see `viewToggleDefaults`). Without this, collapsing the
    // panel from the task view leaves a `cm=0` the URL never gets to state.
    if (!this.showDriveSurface && moved.includes(VIEW_CONTEXT_KEY.task)) {
      moved.push(VIEW_CONTEXT_KEY.comment);
    }

    moved.forEach((key: ViewContextKey) => this.viewContext.markTouched(key));

    this.lastViewToggleState = state;
    this.viewContext.sync(state, this.viewToggleDefaults);
  }

  private updateCommentFeatureVisibility(url: string): void {
    // My Own Space is a single-user drive surface and carries no top-nav
    // comment button at all (STUDIO-2249) — neither the legacy one nor the
    // feed-sidebar toggle that used to stand in for it. The drive explorer's
    // own in-content toolbar is the single entry point to the feed there.
    this.isMySpacePage = this.isMySpacePath(url);
    this.isCommentFeatureAllowed = !this.isMySpacePage && this.commentVisibilityService.isCommentAllowed(url);
    // Nor tasks: its rail has no Tasks button, so a task panel carried over from
    // a project would sit beside the feed with no way to close it.
    this.isTaskFeatureAllowed = this.isCommentFeatureAllowed;
  }

  private isMySpacePath(url: string): boolean {
    return /^\/my-space(\/|$|\?)/.test(url);
  }

  /**
   * A project page whose task panel is not up yet: the project is still loading (the
   * previous page's panel and scope are on screen), or it is a drive project whose
   * surface has not connected the rail or scoped the panel to this project. Opening
   * tasks then shows a panel that is torn down or reloaded a moment later, so `ts`
   * waits for the real one.
   */
  private get isProjectTaskSurfacePending(): boolean {
    const projectId: string | undefined = /^\/projects\/([^/?#]+)/.exec(this.router.url)?.[1];
    if (!projectId) return false;
    if (this.projectService.project?.id !== projectId) return true;
    return (
      this.showDriveSurface &&
      (!this.driveRightRail.visible() || this.platformTaskState.getCurrentValue().projectId !== projectId)
    );
  }

  get showDriveSurface(): boolean {
    return this.projectService.project?.mode === 'drive';
  }

  /**
   * True on org-level pages (brand / drive-mode workspace / campaign) whose chat
   * runs on the drive comment shell — like drive surfaces (STUDIO-2249), the
   * shell owns its own toggle, so the top-nav comment button is hidden.
   */
  get isOrgDriveCommentsActive(): boolean {
    return !!this.platformChatState.getCurrentValue().driveAnchorUuid;
  }

  get isCommentDriveActive(): boolean {
    return this.driveCommentPanelService.active();
  }

  backToAdmin() {
    this.adminService.switchToAdmin();
  }

  onBehalf() {
    return this.authService.isImpersonating();
  }

  get rightViewMode(): string {
    return this.subsectionService.uiState?.rightPanelView;
  }

  openChat() {
    if (this.isMediaPreviewCommentsChatShowing) {
      const currentCollapsed = !!this.subsectionService.uiState?.commentsCollapsed;
      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const finalCollapsed = currentRightPanelView === 'task' ? false : !currentCollapsed;
      this.subsectionService.updateUIState({ commentsCollapsed: finalCollapsed, rightPanelView: 'comment' });
    } else {
      const activePage = this.appState.getActivePage();
      const isExpanded = this.appState.isCommentPageCommentExpanded(activePage);

      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const isExpandedVal = currentRightPanelView === 'task' ? true : !isExpanded;

      this.subsectionService.updateUIState({ rightPanelView: 'comment' });
      this.appState.setCommentPageCommentExpanded(activePage, isExpandedVal);
    }
  }

  openTask() {
    if (this.isMediaPreviewCommentsChatShowing) {
      const currentCollapsed = !!this.subsectionService.uiState?.commentsCollapsed;
      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const finalCollapsed = currentRightPanelView === 'comment' ? false : !currentCollapsed;
      this.subsectionService.updateUIState({ commentsCollapsed: finalCollapsed, rightPanelView: 'task' });
    } else {
      const activePage = this.appState.getActivePage();
      const isExpanded = this.appState.isCommentPageCommentExpanded(activePage);

      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const isExpandedVal = currentRightPanelView === 'comment' ? true : !isExpanded;

      this.subsectionService.updateUIState({ rightPanelView: 'task' });
      this.appState.setCommentPageCommentExpanded(activePage, isExpandedVal);
    }
  }

  changeLang(lang: string, updateSettings: boolean = true) {
    if (this.isPending) {
      return;
    }

    this.isPending = true;
    this.selectedLanguage = lang;
    this.adjustSelectedFlag();
    this.translateService
      .use(lang)
      .then(() => {
        const registerData = JSON.parse(localStorage.getItem('register_data'));
        if (registerData) {
          this.isPending = false;
          return;
        }
        if (updateSettings) {
          this.authService.setAuthLanguage(lang);
        }
        this.notificationService.getAllUnseenNotifications();
        this.isPending = false;
      })
      .catch(() => {
        this.isPending = false;
      });
  }

  adjustSelectedFlag() {
    let flagClass = '';
    switch (this.selectedLanguage.toLowerCase()) {
      case 'fr':
        flagClass = 'flag-icon-fr';
        break;
      case 'es':
        flagClass = 'flag-icon-es';
        break;
      case 'pt':
        flagClass = 'flag-icon-pt';
        break;
      case 'zh':
        flagClass = 'flag-icon-cn';
        break;
      default:
        flagClass = 'flag-icon-us';
        break;
    }
    this.selectedFlag = flagClass;
  }
}
